// ProjectDetailsForm.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import API_BASE from "../config";
import LDTManagerModal from "./LDTManagerModal";
import { NORM_DEFAULTS } from "../config/normDefaults";

// QW-3 (Rainer 29.05.): CCT eines LDT-colour-Eintrags auf reine Kelvin-
// Ziffern normalisieren ("3000K"→"3000", "740"→"4000", "3000"→"3000").
// Bewusst lokale Kopie der korrigierten June-Logik — die normaliseCct()
// im LDTManagerModal (sandbox-Stand) hat noch den 300000K-Anzeigebug;
// beim June-Merge können beide auf eine gemeinsame Util konsolidiert
// werden. Muss inhaltlich zu backend/utils/cct.py passen!
const cctKelvin = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(Array.isArray(raw) ? raw[0] ?? "" : raw).trim().toUpperCase();
  const m = s.match(/^(\d+)\s*K?$/);
  if (!m) return null;
  const code = parseInt(m[1], 10);
  if (code >= 1000 && code <= 9999) return String(code);      // volle Kelvin
  if (code >= 700 && code < 800) return String((code % 100) * 100); // 730→3000, 740→4000
  return null;
};

// `hideProjectFields` toggles off the Name/Number/Address/Town inputs
// once those have already been captured on the CreateProjectPage
// (new two-phase flow, 2026-05-20). The wizard's step 6 then collapses
// to just the calc-time settings (Lichtfarbe; W4 sweep checkbox hidden).
// ── Norm-Vorgaben-Box (2026-08-13) ───────────────────────────────────
// Eigene Komponente mit lokalem Eingabe-Puffer: die Inputs halten den
// ROHEN String (damit "0," beim Tippen nicht sofort zerstört wird),
// nach projectDetails wandert die geparste Zahl — bzw. null, wenn der
// Wert dem Norm-Default entspricht oder das Feld geleert wurde (null =
// "Norm-Default" in DB und Backend).
const normFields = [
  { key: "emTarget", def: NORM_DEFAULTS.em, label: <>E<sub>m</sub></>, op: "≥", unit: "lx", step: 5, title: "Mittlerer Wartungswert" },
  { key: "uoTarget", def: NORM_DEFAULTS.uo, label: <>E<sub>min</sub>/E<sub>m</sub></>, op: "≥", unit: "", step: 0.05, title: "Gleichmäßigkeit" },
  { key: "rgMax", def: NORM_DEFAULTS.rg, label: <>R<sub>G</sub></>, op: "≤", unit: "", step: 1, title: "Blendindex" },
];

const NormTargetsBox = ({ projectDetails, setProjectDetails }) => {
  // Puffer mit den effektiven Werten (editiert oder Default) vorbelegen.
  // ACHTUNG: type="number" verlangt Punkt-Dezimale im value — der Browser
  // zeigt je nach Locale trotzdem ein Komma an.
  const [raw, setRaw] = useState(() =>
    Object.fromEntries(
      normFields.map((f) => [f.key, String(projectDetails[f.key] ?? f.def)])
    )
  );

  // "Bearbeiten"-Fall: der Sketch-Prefill (GET /projects/:id/sketch) kommt
  // NACH dem Mount an. Solange der Nutzer noch nichts angefasst hat, den
  // Puffer mit den nachgeladenen Werten überschreiben — danach nie wieder,
  // sonst kämpft der Sync gegen die Tastatur (Feld leeren unmöglich).
  const touched = useRef(false);
  useEffect(() => {
    if (touched.current) return;
    setRaw(
      Object.fromEntries(
        normFields.map((f) => [f.key, String(projectDetails[f.key] ?? f.def)])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails.emTarget, projectDetails.uoTarget, projectDetails.rgMax]);

  const commit = (field, text) => {
    touched.current = true;
    setRaw((prev) => ({ ...prev, [field.key]: text }));
    const num = parseFloat(text.replace(",", "."));
    setProjectDetails((prev) => ({
      ...prev,
      // Ungültig/leer oder exakt der Default → null = Norm-Default.
      [field.key]: Number.isFinite(num) && num !== field.def ? num : null,
    }));
  };

  const isCustom = normFields.some((f) => projectDetails[f.key] != null);

  return (
    <div style={{ padding: 8, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 12 }}>
          Norm-Vorgaben (EN 12193{isCustom ? ", angepasst" : ", Klasse III"})
        </div>
        {isCustom && (
          <button
            type="button"
            onClick={() => {
              setRaw(Object.fromEntries(normFields.map((f) => [f.key, String(f.def)])));
              setProjectDetails((prev) => ({ ...prev, emTarget: null, uoTarget: null, rgMax: null }));
            }}
            style={{
              fontSize: 11, padding: "1px 6px", borderRadius: 4, cursor: "pointer",
              border: "1px solid #d1d5db", background: "#ffffff", color: "#374151",
            }}
            title="Alle drei Werte auf die Norm-Defaults (75 lx / 0,5 / 55) zurücksetzen"
          >
            ↺ Norm
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {normFields.map((f) => {
          const edited = projectDetails[f.key] != null;
          return (
            <label key={f.key} title={f.title} style={{ flex: 1, fontSize: 11, color: "#374151" }}>
              <span style={{ display: "block", marginBottom: 2 }}>
                {f.label}&nbsp;{f.op}{f.unit ? ` (${f.unit})` : ""}
              </span>
              <input
                type="number"
                step={f.step}
                min={0}
                value={raw[f.key]}
                onChange={(e) => commit(f, e.target.value)}
                // Leeres/ungültiges Feld beim Verlassen wieder mit dem
                // effektiven Wert füllen (null = Norm-Default).
                onBlur={() => {
                  if (!Number.isFinite(parseFloat(raw[f.key].replace(",", ".")))) {
                    setRaw((prev) => ({ ...prev, [f.key]: String(projectDetails[f.key] ?? f.def) }));
                  }
                }}
                style={{
                  width: "100%", boxSizing: "border-box", fontSize: 12, padding: "3px 4px",
                  borderRadius: 4,
                  // Editierte Werte blau markieren, damit man beim Überfliegen
                  // sieht, wo von der Norm abgewichen wurde.
                  border: edited ? "1px solid #2563eb" : "1px solid #d1d5db",
                  color: edited ? "#1d4ed8" : "#111827",
                  fontWeight: edited ? 600 : 400,
                }}
              />
            </label>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
        Vorbelegt mit Klasse III ({NORM_DEFAULTS.em} lx / {String(NORM_DEFAULTS.uo).replace(".", ",")} / {NORM_DEFAULTS.rg}).
        Geänderte Werte gelten für Optimierung <strong>und</strong> Nachweis dieses Projekts.
      </div>
    </div>
  );
};

const ProjectDetailsForm = ({ projectDetails, setProjectDetails, hideProjectFields = false }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectDetails((prev) => ({ ...prev, [name]: value }));
  };

  // ── QW-3: Lichtfarbe als LDT-Vorfilter ─────────────────────────────
  // Wahl wandert als projectDetails.cct ("3000" | "4000" | null=Alle)
  // mit dem Submit-Payload nach /submit bzw. /projects/:id/sketch,
  // wird dort in projects_calc_settings persistiert und schränkt in
  // optimise_light den GA-Kandidatenpool ein — 3000 K und 4000 K
  // werden nie mehr in einer Lösung gemischt.
  const selectedCct = projectDetails.cct ?? null;
  const [luminaires, setLuminaires] = useState([]);
  const [ldtModalOpen, setLdtModalOpen] = useState(false);

  // Aktive LDTs laden, damit wir je Auswahl anzeigen können, wie viele
  // Leuchten der Optimierer tatsächlich zur Verfügung hätte. Refresh
  // nach jedem Modal-Close, weil der Nutzer dort aktiviert/deaktiviert.
  const loadLuminaires = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/luminaires`);
      if (res.ok) setLuminaires(await res.json());
    } catch (e) {
      console.error("LDT-Liste konnte nicht geladen werden", e);
    }
  }, []);
  useEffect(() => { loadLuminaires(); }, [loadLuminaires]);

  // Anzahl aktiver LDTs, die zur gewählten Lichtfarbe passen.
  const activeCount = luminaires.filter(
    (l) => l.enabled && (selectedCct === null || cctKelvin(l.colour) === selectedCct)
  ).length;

  const cctOptions = [
    { value: null, label: "Alle" },
    { value: "3000", label: "3000 K" },
    { value: "4000", label: "4000 K" },
  ];
  // PRD_followups.md §5/W4: opt-out checkbox for the post-GA luminaire
  // sweep. Default ON — if a project hasn't been set explicitly yet,
  // treat the absence (undefined) as TRUE so legacy state still ticks
  // the box on render.
  const handleSweepToggle = (e) => {
    const checked = e.target.checked;
    setProjectDetails((prev) => ({ ...prev, autoLuminaireSweep: checked }));
  };
  const sweepChecked =
    projectDetails.autoLuminaireSweep === undefined
      ? true
      : !!projectDetails.autoLuminaireSweep;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
      {!hideProjectFields && (
        <>
          <label>
            Project Name:
            <input
              type="text"
              name="projectName"
              value={projectDetails.projectName}
              onChange={handleChange}
              placeholder="Enter Project Name"
              style={{ width: "100%" }}
            />
          </label>

          <label>
            Project Number:
            <input
              type="text"
              name="projectNumber"
              value={projectDetails.projectNumber}
              onChange={handleChange}
              placeholder="Enter Project Number"
              style={{ width: "100%" }}
            />
          </label>

          <label>
            Project Address:
            <input
              type="text"
              name="projectAddress"
              value={projectDetails.projectAddress}
              onChange={handleChange}
              placeholder="Enter Project Address"
              style={{ width: "100%" }}
            />
          </label>

          <label>
            Project Town:
            <input
              type="text"
              name="projectTown"
              value={projectDetails.projectTown}
              onChange={handleChange}
              placeholder="Enter Project Town"
              style={{ width: "100%" }}
            />
          </label>
        </>
      )}

      {/* ── QW-3: Lichtfarbe (CCT-Vorfilter für die Optimierung) ───── */}
      <div
        style={{
          padding: "8px", background: "#f9fafb",
          border: "1px solid #e5e7eb", borderRadius: 4,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
          Lichtfarbe
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {cctOptions.map((opt) => {
            const active = selectedCct === opt.value;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() =>
                  setProjectDetails((prev) => ({ ...prev, cct: opt.value }))
                }
                style={{
                  flex: 1, padding: "5px 0", fontSize: 12,
                  borderRadius: 4, cursor: "pointer",
                  border: active ? "1px solid #2563eb" : "1px solid #d1d5db",
                  background: active ? "#2563eb" : "#ffffff",
                  color: active ? "#ffffff" : "#374151",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
          Die Optimierung verwendet nur <strong>aktive</strong> LDTs der
          gewählten Lichtfarbe — 3000 K und 4000 K werden nie gemischt.
        </div>
        {/* Live-Zähler: wie viele aktive LDTs stehen der GA mit dieser
            Wahl zur Verfügung? 0 → rote Warnung, Submit würde mit einer
            klaren Fehlermeldung abbrechen (Guard in optimise_light). */}
        <div
          style={{
            fontSize: 11, marginTop: 4,
            color: activeCount === 0 ? "#dc2626" : "#374151",
            fontWeight: activeCount === 0 ? 600 : 400,
          }}
        >
          {activeCount === 0
            ? "⚠ Keine aktiven LDTs passen zu dieser Auswahl — bitte Bibliothek prüfen!"
            : `${activeCount} aktive LDTs passen zur Auswahl`}
        </div>
        <button
          type="button"
          onClick={() => setLdtModalOpen(true)}
          style={{
            marginTop: 6, width: "100%", padding: "5px 0",
            fontSize: 12, borderRadius: 4, cursor: "pointer",
            border: "1px solid #d1d5db", background: "#ffffff",
            color: "#374151",
          }}
          title="Öffnet die LDT-Bibliothek, um zu prüfen, welche Leuchten aktiv sind"
        >
          LDT-Filter-Einstellungen prüfen
        </button>
      </div>

      {/* ── Norm-Vorgaben EN 12193 (Kundenwunsch 2026-08-13) ─────────
          Die drei Prüfwerte sind mit den Klasse-III-Defaults vorbelegt
          und je Projekt editierbar. Sie steuern BEIDES: das Ziel der
          GA-Optimierung (fitness_func) und den bestanden/empfohlen-
          Check (Sidebar-Ampel, Varianten-Tabelle, Report). */}
      <NormTargetsBox
        projectDetails={projectDetails}
        setProjectDetails={setProjectDetails}
      />

      {/* Bibliotheks-Modal nur zum Prüfen/Anpassen der aktiven LDTs;
          nach dem Schließen Zähler aktualisieren. */}
      <LDTManagerModal
        isOpen={ldtModalOpen}
        onClose={() => { setLdtModalOpen(false); loadLuminaires(); }}
      />

      {/* Auto-sweep checkbox HIDDEN 2026-05-20 — Philip: "deactivate
          the multi luminaire idea for now". Backend gate in
          optimise_light.py also disables the trigger so even legacy
          projects with auto_luminaire_sweep=TRUE skip the sweep.
          Re-enable both when the feature is ready. */}
      {false && (
      <>
      {/* PRD_followups.md §5/W4: auto-sweep opt-out checkbox.
          Defaults ON; user unticks to skip the Strategy-A sweep after
          the primary GA. Stored on the Project row (auto_luminaire_sweep)
          via the autoLuminaireSweep field in ProjectData. */}
      <label
        style={{
          display: "flex", alignItems: "flex-start", gap: 6,
          padding: "6px 8px", background: "#f9fafb",
          border: "1px solid #e5e7eb", borderRadius: 4,
          fontSize: 12, cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          name="autoLuminaireSweep"
          checked={sweepChecked}
          onChange={handleSweepToggle}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span>
          <strong>Automatisch alternative Leuchten prüfen</strong>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
            Nach der ersten Berechnung werden im Hintergrund Varianten
            mit anderen Leuchten aus der LDT-Bibliothek erzeugt
            (Strategie A, ~1 min je Lauf).
          </div>
        </span>
      </label>
      </>
      )}

    </div>
  );
};

export default ProjectDetailsForm;